import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { Lock, LogIn } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

type AppNotification = {
  _id: string;
  title: string;
  body: string;
  route?: string;
  type?: string;
  category?: string;
  priority?: string;
  readAt?: string | null;
  archivedAt?: string | null;
  createdAt: string;
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1/notifications`;
const cardsBorderRadii = "8px";
const formatDateTime = (value: string) => {
  const date = new Date(value);
  return date.toLocaleString();
};

const categoryColorScheme = (category?: string) => {
  switch (category) {
    case "support":
      return "red";
    case "delivery":
      return "orange";
    case "order":
      return "blue";
    case "product":
      return "green";
    case "accommodation":
      return "purple";
    case "car_hire":
      return "yellow";
    case "hike":
      return "cyan";
    case "system":
      return "gray";
    default:
      return "gray";
  }
};

const formatCategoryLabel = (category?: string) => {
  switch (category) {
    case "order":
      return "Orders";
    case "delivery":
      return "Deliveries";
    case "product":
      return "Products";
    case "accommodation":
      return "Accommodation";
    case "car_hire":
      return "Car Hire";
    case "hike":
      return "Trips";
    case "support":
      return "Support";
    case "system":
      return "System";
    case "general":
      return "General";
    default:
      return category || "General";
  }
};

const CATEGORY_TABS = [
  { value: "all", label: "All" },
  { value: "order", label: "Orders" },
  { value: "delivery", label: "Deliveries" },
  { value: "product", label: "Products" },
  { value: "accommodation", label: "Accommodations" },
  { value: "car_hire", label: "Car Hire" },
  { value: "hike", label: "Trips" },
  { value: "support", label: "Support" },
  { value: "system", label: "System" },
  { value: "general", label: "General" },
];

const NotificationsPage: React.FC = () => {
  const authContext = useAuth() as any;
  const user = authContext?.user;
  const authLoading = authContext?.loading || authContext?.isLoading || false;

  const { theme } = useCustomTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "read" | "unread" | "archived"
  >("all");

  const userId = user?.uid || "";

  const pageBg = theme.background;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;
  const subtleText = theme.textMuted;
  const headingText = theme.text;
  const cardShadow = theme.shadowSm;
  const panelShadow = theme.shadowMd;
  const isDark = theme.name === "dark";

  const queryParams = useMemo(() => {
    return {
      userId,
      page,
      limit: 20,
      unreadOnly: statusFilter === "unread",
      readOnly: statusFilter === "read",
      archived: statusFilter === "archived",
      type: typeFilter === "all" ? "" : typeFilter,
      category: categoryFilter === "all" ? "" : categoryFilter,
    };
  }, [userId, page, statusFilter, typeFilter, categoryFilter]);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const res = await axios.get(API_BASE, {
        params: queryParams,
      });

      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setHasMore(Boolean(res.data?.hasMore));
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, queryParams]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleOpen = async (item: AppNotification) => {
    try {
      if (!item.readAt) {
        await axios.patch(`${API_BASE}/${item._id}/read`, { userId });
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    } finally {
      setItems((prev) =>
        prev.map((entry) =>
          entry._id === item._id
            ? { ...entry, readAt: entry.readAt || new Date().toISOString() }
            : entry,
        ),
      );

      if (item.route) {
        navigate(item.route);
      }
    }
  };

  const handleArchive = async (itemId: string) => {
    try {
      await axios.patch(`${API_BASE}/${itemId}/archive`, { userId });

      if (statusFilter === "archived") {
        setItems((prev) =>
          prev.map((entry) =>
            entry._id === itemId
              ? {
                  ...entry,
                  archivedAt: entry.archivedAt || new Date().toISOString(),
                }
              : entry,
          ),
        );
      } else {
        setItems((prev) => prev.filter((entry) => entry._id !== itemId));
      }
    } catch (error) {
      console.error("Failed to archive notification:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.patch(`${API_BASE}/read-all`, { userId });
      setItems((prev) =>
        prev.map((item) => ({
          ...item,
          readAt: item.readAt || new Date().toISOString(),
        })),
      );
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const handleArchiveAll = async () => {
    try {
      await axios.patch(`${API_BASE}/archive-all`, { userId });

      if (statusFilter === "archived") {
        await fetchNotifications();
      } else {
        setItems([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to archive all notifications:", error);
    }
  };

  const canArchiveAll = statusFilter !== "archived" && items.length > 0;
  const canMarkAllRead =
    statusFilter !== "archived" && items.some((item) => !item.readAt);

  if (authLoading) {
    return (
      <Flex align="center" justify="center" py={10} gap={2}>
        <Spinner size="sm" />
        <Text color={theme.textSubtle}>Checking account...</Text>
      </Flex>
    );
  }

  if (!userId) {
    return (
      <Box
        bg={pageBg}
        minH="100%"
        p={{ base: 4, md: 6 }}
        color={theme.text}
        borderRadius={cardsBorderRadii}
      >
        <Box mx="auto">
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius={cardsBorderRadii}
            p={{ base: 4, md: 6 }}
            boxShadow={panelShadow}
            mb={6}
            position="relative"
            overflow="hidden"
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              gap={4}
            >
              <Box>
                <HStack spacing={2} mb={2} flexWrap="wrap">
                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(39,183,255,0.14)" : "rgba(39,183,255,0.08)"
                    }
                    color={isDark ? "blue.200" : "blue.700"}
                    fontWeight="700"
                  >
                    Notifications
                  </Badge>

                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(239,68,68,0.16)" : "rgba(239,68,68,0.10)"
                    }
                    color={isDark ? "red.200" : "red.700"}
                    fontWeight="700"
                  >
                    Login required
                  </Badge>
                </HStack>

                <Heading size="lg" color={headingText}>
                  Sign in to view your notifications
                </Heading>

                <Text mt={2} color={subtleText} fontWeight="500" maxW="760px">
                  Your notifications are private. Sign in first to view your
                  order updates, delivery alerts, booking messages, support
                  updates, and system notifications.
                </Text>
              </Box>

              <HStack
                spacing={2}
                px={3}
                py={2}
                borderRadius={cardsBorderRadii}
                border={`1px solid ${borderColor}`}
                bg={isDark ? "rgba(255,255,255,0.03)" : softBg}
                boxShadow={cardShadow}
              >
                <Box w="8px" h="8px" borderRadius={cardsBorderRadii} bg={theme.primary} />
                <Text color={headingText} fontWeight="700" fontSize="sm">
                  Secure notifications
                </Text>
              </HStack>
            </Flex>
          </Box>

          <Box
            borderRadius={cardsBorderRadii}
            bg={cardBg}
            boxShadow={cardShadow}
            border={`1px solid ${borderColor}`}
            p={{ base: 5, md: 6 }}
          >
            <VStack spacing={4} align="start">
              <Flex
                w="56px"
                h="56px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)"}
                border={`1px solid ${
                  isDark ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.16)"
                }`}
              >
                <Icon
                  as={Lock}
                  boxSize={6}
                  color={isDark ? "red.200" : "red.600"}
                />
              </Flex>

              <Box>
                <Text color={headingText} fontWeight="800" fontSize="lg">
                  You need to be logged in
                </Text>
                <Text color={subtleText} mt={1}>
                  Sign in to view notifications linked to your account and keep
                  track of important PickDengu updates.
                </Text>
              </Box>

              <HStack spacing={3} pt={1} flexWrap="wrap">
                <Button
                  leftIcon={<LogIn size={16} />}
                  color={theme.textOnPrimary}
                  bgImage={theme.gradients?.primaryButton || theme.primary}
                  _hover={{
                    bgImage:
                      theme.gradients?.primaryButtonHover || theme.primaryHover,
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  borderRadius={cardsBorderRadii}
                  boxShadow={theme.shadowSm}
                  transition="all 0.2s ease"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>

                <Button
                  variant="outline"
                  borderColor={borderStrong}
                  color={headingText}
                  bg={cardBg}
                  _hover={{ bg: theme.hover }}
                  borderRadius={cardsBorderRadii}
                  onClick={() => navigate("/dashboard")}
                >
                  Go back
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box w="100%" mx="auto" p={{ base: 4, md: 6 }} color={theme.text}>
      <VStack align="stretch" spacing={5}>
        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={{ base: 4, md: 5 }}
          boxShadow={theme.shadowSm}
        >
          <Flex
            justify="space-between"
            align={{ base: "start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <VStack align="start" spacing={1}>
              <Text
                fontSize={{ base: "2xl", md: "3xl" }}
                fontWeight="bold"
                color={theme.text}
              >
                Notifications
              </Text>
              <Text color={theme.textSubtle}>
                View all your updates, alerts, and system messages.
              </Text>
            </VStack>

            <HStack spacing={3} flexWrap="wrap">
              <Button
                onClick={handleMarkAllRead}
                variant="outline"
                borderColor={theme.borderStrong}
                color={theme.text}
                _hover={{ bg: theme.hover }}
                isDisabled={!canMarkAllRead}
              >
                Mark all read
              </Button>

              <Button
                onClick={handleArchiveAll}
                variant="outline"
                borderColor={theme.borderStrong}
                color={theme.text}
                _hover={{ bg: theme.hover }}
                isDisabled={!canArchiveAll}
              >
                Clear all
              </Button>
            </HStack>
          </Flex>
        </Box>

        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={{ base: 4, md: 5 }}
          boxShadow={theme.shadowSm}
        >
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 2, md: 5, xl: 10 }} spacing={2}>
              {CATEGORY_TABS.map((tab) => {
                const active = categoryFilter === tab.value;
                return (
                  <Button
                    key={tab.value}
                    onClick={() => {
                      setPage(1);
                      setCategoryFilter(tab.value);
                      setTypeFilter("all");
                    }}
                    variant={active ? "solid" : "outline"}
                    color={active ? theme.textOnPrimary : theme.text}
                    bg={active ? theme.primary : "transparent"}
                    borderColor={theme.borderStrong}
                    _hover={{
                      bg: active ? theme.primary : theme.hover,
                    }}
                    size="sm"
                  >
                    {tab.label}
                  </Button>
                );
              })}
            </SimpleGrid>

            <Flex gap={3} direction={{ base: "column", md: "row" }}>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(
                    e.target.value as "all" | "read" | "unread" | "archived",
                  );
                }}
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
                color={theme.text}
              >
                <option value="all">All active notifications</option>
                <option value="read">Read only</option>
                <option value="unread">Unread only</option>
                <option value="archived">Archived</option>
              </Select>

              <Select
                value={typeFilter}
                onChange={(e) => {
                  setPage(1);
                  setTypeFilter(e.target.value);
                  setCategoryFilter("all");
                }}
                bg={theme.inputBg}
                borderColor={theme.inputBorder}
                color={theme.text}
              >
                <option value="all">All event types</option>
                <option value="seller_new_paid_order">Seller: New paid order</option>
                <option value="order_status_changed">Orders</option>
                <option value="delivery_status_changed">Deliveries</option>
                <option value="hire_booking_status_changed">Car hire</option>
                <option value="trip_booking_status_changed">Hikes</option>
                <option value="accommodation_booking_status_changed">
                  Accommodations
                </option>
                <option value="support_ticket_created">Support Created</option>
                <option value="support_ticket_assigned">
                  Support Assigned
                </option>
                <option value="support_ticket_resolved">
                  Support Resolved
                </option>
                <option value="system_message">System</option>
              </Select>
            </Flex>
          </VStack>
        </Box>

        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          overflow="hidden"
          boxShadow={theme.shadowSm}
        >
          {loading ? (
            <Flex align="center" justify="center" py={10} gap={2}>
              <Spinner size="sm" />
              <Text color={theme.textSubtle}>Loading notifications...</Text>
            </Flex>
          ) : items.length === 0 ? (
            <Box p={8}>
              <Text textAlign="center" color={theme.textSubtle}>
                No notifications found.
              </Text>
            </Box>
          ) : (
            items.map((item) => (
              <Box
                key={item._id}
                px={{ base: 4, md: 5 }}
                py={4}
                borderBottom="1px solid"
                borderColor={theme.border}
                _last={{ borderBottom: "none" }}
                bg={
                  statusFilter !== "archived" && !item.readAt
                    ? theme.selected
                    : "transparent"
                }
              >
                <Flex
                  justify="space-between"
                  align={{ base: "start", md: "center" }}
                  direction={{ base: "column", md: "row" }}
                  gap={4}
                >
                  <Box flex={1}>
                    <HStack spacing={2} mb={2} flexWrap="wrap">
                      <Text
                        fontWeight={item.readAt ? "semibold" : "bold"}
                        color={theme.text}
                      >
                        {item.title}
                      </Text>

                      {statusFilter !== "archived" && !item.readAt && (
                        <Badge colorScheme="green">Unread</Badge>
                      )}

                      {statusFilter !== "archived" && item.readAt && (
                        <Badge colorScheme="gray">Read</Badge>
                      )}

                      {statusFilter === "archived" && (
                        <Badge colorScheme="orange">Archived</Badge>
                      )}

                      {item.category && (
                        <Badge colorScheme={categoryColorScheme(item.category)}>
                          {formatCategoryLabel(item.category)}
                        </Badge>
                      )}

                      {item.priority && (
                        <Badge
                          colorScheme={
                            item.priority === "urgent"
                              ? "red"
                              : item.priority === "high"
                                ? "orange"
                                : "purple"
                          }
                        >
                          {item.priority}
                        </Badge>
                      )}
                    </HStack>

                    <Text color={theme.textMuted} mb={2}>
                      {item.body}
                    </Text>

                    <Text fontSize="sm" color={theme.textSubtle}>
                      {formatDateTime(item.createdAt)}
                    </Text>
                  </Box>

                  <VStack
                    align="stretch"
                    spacing={2}
                    minW={{ base: "100%", md: "180px" }}
                  >
                    <Button
                      onClick={() => handleOpen(item)}
                      color={theme.textOnPrimary}
                      bgImage={theme.gradients.primaryButton}
                      _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                      boxShadow={theme.shadowSm}
                    >
                      Open
                    </Button>

                    {statusFilter !== "archived" && (
                      <Button
                        onClick={() => handleArchive(item._id)}
                        variant="outline"
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                      >
                        Archive
                      </Button>
                    )}
                  </VStack>
                </Flex>
              </Box>
            ))
          )}
        </Box>

        <HStack justify="space-between">
          <Button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            isDisabled={page <= 1 || loading}
            variant="outline"
            borderColor={theme.borderStrong}
            color={theme.text}
            _hover={{ bg: theme.hover }}
          >
            Previous
          </Button>

          <Text color={theme.textSubtle}>Page {page}</Text>

          <Button
            onClick={() => setPage((prev) => prev + 1)}
            isDisabled={!hasMore || loading}
            variant="outline"
            borderColor={theme.borderStrong}
            color={theme.text}
            _hover={{ bg: theme.hover }}
          >
            Next
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default NotificationsPage;
